import {
  fileUploadProbeGetSampleWithFile,
  fileUploadProbeRemoveStage,
  probeGetSample,
  probeRemoveRestStage,
} from 'js/actions/probe';
import useAppDispatch from 'js/hooks/useAppDispatch';
import UIUtils from 'js/lib/UIUtils';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Delay between follow-up polls while a probe job is still 202 (processing). */
export const TEST_POLL_INTERVAL_MS = 10000;
/**
 * Max follow-up polls (~10 min at {@link TEST_POLL_INTERVAL_MS}) for callers that
 * opt into a cap via `maxPollAttempts` (BYO, NEX-22636). Without the option the
 * loop re-polls until the job leaves 202, as before.
 */
export const TEST_POLL_MAX_ATTEMPTS = 60;
export const TEST_POLL_TIMEOUT_MESSAGE =
  'The test run is taking too long and was stopped. Try again, or test on a smaller file.';

/** A container's `code_config` (and a test run's `code_container_config`): never undefined fields. */
export function buildCodeContainerConfig(packages?: string | null, extraData?: string | null) {
  return { packages: packages || '', extra_data: extraData || '' };
}

/**
 * Hook for testing custom processor code against sample files.
 * Used in BYO Flow, DirectFlow, FlexFlow, and Reusable Transforms editing.
 *
 * @param credentialId - The credential ID to probe
 * @param useFileUploadProbe - Use isolated File Upload probe Redux slice/actions.
 * @param getProbeSampleCallback - Optional callback for making probe/sample API calls.
 * @param options.maxPollAttempts - Give up after this many follow-up polls. Unset = no cap.
 *
 * ## Why getProbeSampleCallback exists:
 * - DirectFlow/BYO uses the adapter pattern (useProbeAdapter) for architectural consistency
 * - FlexFlow and Reusable Transforms use direct Redux dispatch (no callback needed)
 * - When provided, the callback is used instead of direct probeGetSample dispatch
 * - Both paths call the same API, this is purely for code organization
 *
 * ## Callers:
 * - DirectFlow/BYO (SourceConfigureStep.tsx): passes getProbeSampleCallback from useProbeAdapter
 * - FlexFlow (AddDataSourceForm.js): does NOT pass callback
 * - Reusable Transforms (EditCustomProcessor.tsx): does NOT pass callback
 */
export default function useTestCustomProcessor(
  credentialId: number,
  useFileUploadProbe = false,
  getProbeSampleCallback?: (credentialId: number, stage: string, payload: any) => void,
  { maxPollAttempts }: { maxPollAttempts?: number } = {},
) {
  const dispatch = useAppDispatch();

  const probeResourceId = useMemo(() => {
    if (!useFileUploadProbe) return credentialId;
    if (credentialId != null) return credentialId;
    return 'file_upload';
  }, [useFileUploadProbe, credentialId]);

  // `parserOutput`/`testResult` used to come straight from Redux selectors
  // keyed only by `credentialId` — with no per-run identity, two overlapping
  // runs (e.g. Run → Cancel → Run again before the first request settled, or
  // simply clicking Run twice) both wrote into the same slot, so whichever
  // response resolved last won the screen regardless of which run the user
  // actually cared about. A stuck/cancelled run's late response could
  // silently clobber a newer run's real result.
  //
  // Fixed by making these hook-instance-local state, committed only from a
  // dispatch whose `runIdRef` generation still matches the run that issued
  // it (see `test`/`poll` below) — a stale run's response is simply dropped
  // instead of being written where a newer run's result is displayed.
  const [localParserOutput, setLocalParserOutput] = useState<any>(null);
  const [localTestResult, setLocalTestResult] = useState<any>(null);
  const [isLoadingParserOutput, setIsLoadingParserOutput] = useState(false);
  const [isLoadingTestResult, setIsLoadingTestResult] = useState(false);
  // Surfaces a transport/network failure mid-poll (e.g. connection drop) — previously
  // only console.log'd, leaving loading flags flip back to false with no indication
  // anything went wrong. Cleared at the start of every test() run.
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(false);
  // Set by cancel() so `poll` stops recursing immediately, instead of only on
  // unmount. Unlike `isMounted`, this can be triggered while the component
  // (and the run) is still fully mounted and visible — e.g. a user-facing
  // Cancel action — so an in-flight run doesn't keep polling in the
  // background after the user asked it to stop.
  const cancelRequested = useRef(false);
  // Bumped on every test() call (and on cancel()) — the run's own generation
  // number. A response is only committed to local state while it still
  // matches the CURRENT generation; an older run's straggling response, no
  // matter when it resolves, is discarded rather than overwriting a newer
  // run's output.
  const runIdRef = useRef(0);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  /** Stop polling for the current run. Does not abort the in-flight HTTP
   * request that's already on the wire, but guarantees no further poll
   * requests are made once it resolves, and no further commits either. */
  const cancel = () => {
    cancelRequested.current = true;
    runIdRef.current += 1;
    setIsLoadingParserOutput(false);
    setIsLoadingTestResult(false);
  };

  /**
   * Executes custom processor test flow for both parser and post-processor stages.
   *
   * Behavior:
   * - S3/standard connectors: requires `credentialId` and uses legacy probe actions.
   * - File Upload mode: requires `selectedFile` and uses isolated file upload probe actions.
   * - Clears previous `_parserOutput` and `_testResult` stages before running to avoid stale results.
   *
   * @param codeBase64 Base64 encoded custom processor script.
   * @param path Source path or selected file name shown in UI.
   * @param sourceInfo Source and code container config used in probe payload.
   * @param fetchParserOutput Whether to fetch parser output stage before test result.
   * @param selectedFile Browser File object for file upload probe mode.
   */
  const test = (
    codeBase64: string,
    path: string,
    sourceInfo: {
      source_config: any;
      code_container_config: any;
    },
    fetchParserOutput = true,
    selectedFile?: File,
  ) => {
    if (!useFileUploadProbe && !credentialId) return;
    if (useFileUploadProbe && !selectedFile) return;

    const myRunId = ++runIdRef.current;
    cancelRequested.current = false;
    setError(null);
    setLocalParserOutput(null);
    setLocalTestResult(null);

    if (useFileUploadProbe) {
      dispatch(fileUploadProbeRemoveStage(probeResourceId, '_parserOutput'));
      dispatch(fileUploadProbeRemoveStage(probeResourceId, '_testResult'));
    } else {
      dispatch(probeRemoveRestStage(credentialId, '_parserOutput'));
      dispatch(probeRemoveRestStage(credentialId, '_testResult'));
    }

    const { source_config, code_container_config } = sourceInfo;

    // `ui.byo_code_predefined_id` is a BYO-only UI bookkeeping marker
    // (useByoSourceSave.ts writes it, useByoEditSource.ts reads it back to
    // restore the right "Use Existing"/"Provide Your Own" tab) — no probe
    // endpoint needs it, and it rejects the value outright when it's
    // `null` ("Non-string value found in original settings for key
    // ui.byo_code_predefined_id: null"), 500ing the test run before the
    // user's code ever executes. Stripped here (the one place every
    // caller — BYO, DirectFlow, FlexFlow, Reusable Transforms — funnels
    // through) rather than in each caller's own source_config assembly.
    const probeSourceConfig = { ...source_config };
    delete probeSourceConfig['ui.byo_code_predefined_id'];

    const payload: any = {
      path,
      mode: 'custom',
      source_config: probeSourceConfig,
      code_container_config,
    };

    if (fetchParserOutput) {
      runProbeStage('_parserOutput', payload, myRunId, selectedFile, {
        setOutput: setLocalParserOutput,
        setLoading: setIsLoadingParserOutput,
        failureMessage: 'Parser output request failed.',
      });
    }

    runProbeStage(
      '_testResult',
      { ...payload, code: { language: 'python', encoding: 'base64', script: codeBase64 } },
      myRunId,
      selectedFile,
      {
        setOutput: setLocalTestResult,
        setLoading: setIsLoadingTestResult,
        failureMessage: 'Test run request failed.',
      },
    );
  };

  /**
   * The probe/sample request for `stage`: the isolated File Upload probe, the
   * caller's adapter (DirectFlow/BYO, via useProbeAdapter), or a direct
   * Redux dispatch (FlexFlow/Reusable Transforms). Same API either way.
   */
  const sampleRequest = (stage: string, payload, selectedFile?: File) => {
    if (useFileUploadProbe) {
      return fileUploadProbeGetSampleWithFile(probeResourceId, stage, selectedFile, payload);
    }
    if (getProbeSampleCallback) {
      return () => getProbeSampleCallback(credentialId, stage, payload);
    }
    return probeGetSample(credentialId, stage, payload);
  };

  /**
   * Runs one probe stage in two phases:
   * 1. INITIAL request with force:true — starts a NEW probe job (without it the
   *    backend may return cached/stale results; NEX-19987).
   * 2. While that returns 202 (processing), POLL without force — checks the
   *    existing job instead of starting another.
   * Every request path gets the same payloads (see sampleRequest).
   */
  const runProbeStage = async (
    stage: '_parserOutput' | '_testResult',
    payload,
    myRunId: number,
    selectedFile: File | undefined,
    {
      setOutput,
      setLoading,
      failureMessage,
    }: {
      setOutput: (output: any) => void;
      setLoading: (loading: boolean) => void;
      failureMessage: string;
    },
  ) => {
    setLoading(true);
    try {
      const initialResponse = await dispatch(
        sampleRequest(stage, { ...payload, force: true }, selectedFile),
      );
      const initialOutput = initialResponse?.result?.output;
      if (runIdRef.current === myRunId) setOutput(initialOutput);

      if (initialOutput?.statusCode === 202) {
        await poll(
          sampleRequest(stage, payload, selectedFile),
          TEST_POLL_INTERVAL_MS,
          myRunId,
          setOutput,
        );
      }
    } catch (err) {
      console.log('Poll error', err);
      if (runIdRef.current === myRunId) {
        setError(err instanceof Error ? err.message : failureMessage);
      }
    } finally {
      if (runIdRef.current === myRunId) setLoading(false);
    }
  };

  /**
   * Polls `request` every `delay` ms until it resolves with a non-202 status,
   * giving up (throwing {@link TEST_POLL_TIMEOUT_MESSAGE}, surfaced via `error`
   * by the caller's catch) after `maxPollAttempts` requests when set.
   * Every resolved response — intermediate 202s included — is committed via
   * `onUpdate`, but only while `myRunId` still matches the live generation
   * (see `runIdRef` above); a response from a run that's since been
   * cancelled or superseded is dropped instead of committed.
   */
  const poll = async (
    request,
    delay: number,
    myRunId: number,
    onUpdate: (output: any) => void,
    attempt = 1,
  ) => {
    if (!isMounted.current || cancelRequested.current || runIdRef.current !== myRunId) {
      return;
    }

    const {
      result: { output },
    } = await dispatch(request);

    // A newer run may have started (or this one been cancelled) while this
    // request was in flight — never let a stale run's response land.
    if (runIdRef.current !== myRunId) {
      return;
    }
    onUpdate(output);

    if (output?.statusCode === 202) {
      if (maxPollAttempts != null && attempt >= maxPollAttempts) {
        throw new Error(TEST_POLL_TIMEOUT_MESSAGE);
      }
      await UIUtils.wait(delay);
      return poll(request, delay, myRunId, onUpdate, attempt + 1);
    }

    return output?.response;
  };

  return {
    isLoadingParserOutput,
    isLoadingTestResult,
    parserOutput: localParserOutput,
    testResult: localTestResult,
    error,
    test,
    cancel,
  };
}
